/**
 * WakeWordEngine.js
 * 
 * Hybrid Wake Word Engine that automatically selects the best available technology
 * for the user's device to provide "Hey Kisan" keyword spotting.
 * 
 * - Primary: Web Speech API (NativeSpeechWakeWordDetector) for Chrome, Edge, Safari 14.5+
 * - Fallback: Acoustic Cadence (LocalWakeWordDetector) for Firefox or older browsers
 */

import { NativeSpeechWakeWordDetector } from "./NativeSpeechWakeWordDetector";
import { LocalWakeWordDetector } from "./LocalWakeWordDetector";

export class WakeWordEngine {
  constructor(options = {}) {
    this.onWakeWord = options.onWakeWord || (() => {});
    this.sensitivity = options.sensitivity || 0.75;
    
    // Determine capabilities
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    this.isNativeSupported = !!SpeechRecognition;
    
    if (this.isNativeSupported) {
      console.log("[WakeWordEngine] Using Native Speech API for wake word.");
      this.detector = new NativeSpeechWakeWordDetector({
        onWakeWord: this.onWakeWord
      });
    } else {
      console.log("[WakeWordEngine] Native Speech API unavailable. Using Acoustic fallback.");
      this.detector = new LocalWakeWordDetector({
        onWakeWord: this.onWakeWord,
        sensitivity: this.sensitivity
      });
    }
  }

  /**
   * Starts the wake word listener.
   */
  start() {
    return this.detector.start();
  }

  /**
   * Stops the wake word listener and releases resources (especially microphone).
   */
  stop() {
    this.detector.stop();
  }

  /**
   * Locks the detector to prevent triggers (e.g. while Assistant is speaking).
   */
  lock() {
    this.detector.lock();
  }

  /**
   * Unlocks the detector to allow triggers again.
   */
  unlock() {
    this.detector.unlock();
  }

  /**
   * For the acoustic fallback, it needs raw audio buffers.
   * If using native, this does nothing because native handles its own audio stream.
   */
  feedAudio(int16Array) {
    if (!this.isNativeSupported && this.detector.feedAudio) {
      return this.detector.feedAudio(int16Array);
    }
    return false;
  }

  destroy() {
    this.detector.destroy();
  }
}

export default WakeWordEngine;
